import axios from 'axios';
import moment from 'moment';
import React from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import toastr from 'toastr';

import NotFound from './NotFound';
import CanonicalLinks from '../components/common/CanonicalLinks';
import ErrorBoundary from '../components/common/ErrorBoundary';
import ErrorMessage from '../components/common/ErrorMessage';
import Icon from '../components/common/Icon';
import Loading from '../components/common/Loading';
import EpisodeList from '../components/episodes/List';
import Detail from '../components/events/Detail';
import Dialog from '../components/techniques/Dialog';
import { hasConcluded } from '../helpers/Event';
import {
	mayEditContent,
} from '../helpers/permissions';
import { getTranslation } from '../helpers/Technique';
import { useUser } from '../hooks/user';
import i18n from '../i18n';

export const Component = () => {
	const params = useParams();
	const { name } = params;
	const { user } = useUser();
	const { t } = useTranslation();

	const [error, setError] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [event, setEvent] = React.useState(null);

	const [editContent, setEditContent] = React.useState(null);
	const [episodes, setEpisodes] = React.useState([]);
	const [pastMode, setPastMode] = React.useState(false);
	const [showContentDialog, setShowContentDialog] = React.useState(false);

	const actions = React.useMemo(() => ({
		editContent: mayEditContent(user) ? content => {
			setEditContent(content);
			setShowContentDialog(true);
		} : null,
	}), [user]);

	const fetchEpisodes = React.useCallback((controller, event) => {
		if (!event) {
			setEpisodes([]);
			return;
		}
		const params = {
			event: [event.id],
		};
		if (hasConcluded(event)) {
			params.limit = 25;
			params.reverse = '1';
		} else if (pastMode) {
			params.before = moment().add(3, 'hours').toISOString();
			params.limit = 25;
			params.reverse = '1';
		} else {
			params.after = moment().subtract(3, 'hours').toISOString();
			params.before = moment().add(14, 'days').toISOString();
		}
		axios.get(`/api/episodes`, {
			signal: controller.signal,
			params,
		}).then(response => {
			setEpisodes(response.data || []);
		}).catch(e => {
			if (!axios.isCancel(e)) {
				console.error(e);
			}
		});
	}, [pastMode]);

	const saveContent = React.useCallback(async values => {
		try {
			const response = await axios.put(`/api/content/${values.id}`, {
				parent_id: event.description_id,
				...values,
			});
			toastr.success(t('content.saveSuccess'));
			setEvent(event => ({
				...event,
				description: response.data,
			}));
			setShowContentDialog(false);
		} catch (e) {
			toastr.error(t('content.saveError'));
		}
	}, [event && event.description_id]);

	React.useEffect(() => {
		const ctrl = new AbortController();
		setLoading(true);
		axios
			.get(`/api/events/${name}`, { signal: ctrl.signal })
			.then(response => {
				setError(null);
				setLoading(false);
				setEvent(response.data);
			})
			.catch(error => {
				setError(error);
				setLoading(false);
				setEvent(null);
			});
		return () => {
			ctrl.abort();
		};
	}, [name]);

	React.useEffect(() => {
		const controller = new AbortController();
		fetchEpisodes(controller, event);
		const timer = setInterval(() => {
			fetchEpisodes(controller, event);
		}, 1.5 * 60 * 1000);
		return () => {
			controller.abort();
			clearInterval(timer);
		};
	}, [event, fetchEpisodes]);

	if (loading) {
		return <Loading />;
	}

	if (error) {
		return <ErrorMessage error={error} />;
	}

	if (!event) {
		return <NotFound />;
	}

	return <ErrorBoundary>
		<Helmet>
			<title>
				{(event.description && getTranslation(event.description, 'title', i18n.language))
					|| event.title}
			</title>
		</Helmet>
		{event.description ? <Helmet>
			<meta
				name="description"
				content={getTranslation(event.description, 'short', i18n.language)}
			/>
		</Helmet> : null}
		<CanonicalLinks base={`/events/${event.name}`} />
		<Container>
			<Detail actions={actions} event={event} />
			<div className="d-flex align-items-center justify-content-between">
				<h2 className="mt-4">
					{t(pastMode || hasConcluded(event)
						? 'events.pastEpisodes'
						: 'events.upcomingEpisodes'
					)}
				</h2>
				<div className="button-bar">
					{!hasConcluded(event) ?
						<Button
							className="ms-3"
							onClick={() => setPastMode(!pastMode)}
							title={t(pastMode ? 'events.setFutureMode' : 'events.setPastMode')}
							variant="outline-secondary"
						>
							<Icon.TIME_REVERSE title="" />
						</Button>
					: null}
				</div>
			</div>
			{episodes.length ?
				<EpisodeList episodes={episodes} />
			:
				<Alert variant="info">
					{t(pastMode ? 'events.noPastEpisodes' : 'events.noUpcomingEpisodes')}
				</Alert>
			}
		</Container>
		<Dialog
			content={editContent}
			language={i18n.language}
			onHide={() => { setShowContentDialog(false); }}
			onSubmit={saveContent}
			show={showContentDialog}
		/>
	</ErrorBoundary>;
};
